import * as React from "react";

import { Button, Input, notify } from "Components/ui";
import { ArrowUpFromLine, Check, Trash2 } from "Components/ui/icons";
import { profilePics } from "utils";
import { cn } from "utils/cn";
import type { ProfileData } from "../data";
import { preparePhoto } from "../photo";

interface PhotoOptionProps {
  src: string;
  label: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}

const PhotoOption: React.FC<PhotoOptionProps> = ({ src, label, selected, disabled, onSelect }) => (
  <Button
    variant="ghost"
    aria-label={label}
    aria-pressed={selected}
    disabled={disabled}
    onClick={onSelect}
    className={cn(
      "relative aspect-square h-auto w-full rounded-full p-1",
      selected && "ring-2 ring-primary"
    )}
  >
    <img src={src} alt="" className="h-full w-full rounded-full bg-surface-muted object-cover" />
    {selected && (
      <span className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Check aria-hidden="true" weight="bold" className="!h-3 !w-3" />
      </span>
    )}
  </Button>
);

interface PhotoPickerProps {
  profile: ProfileData;
  onDone: () => void;
}

type Busy = null | "upload" | "remove" | number;

/**
 * Upload a photo (cropped and resized here, saved on the account) or pick a stock one, in place
 * under the account header of the profile drawer.
 */
const PhotoPicker: React.FC<PhotoPickerProps> = ({ profile, onDone }) => {
  const { user, uploadPhoto, chooseStockPhoto, removePhoto } = profile;
  const fileInput = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = React.useState<Busy>(null);

  const uploaded = user?.photo || null;
  const stockIndex =
    user?.profilePicIndex === null || user?.profilePicIndex === undefined
      ? null
      : Number(user.profilePicIndex);

  const run = async (next: Busy, action: () => Promise<void>, done: string) => {
    setBusy(next);
    try {
      await action();
      notify.success(done);
    } catch (err) {
      notify.error(err, { fallback: "Your photo couldn't be saved" });
    } finally {
      setBusy(null);
    }
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Let the same file be picked again later.
    event.target.value = "";
    if (!file) {
      return;
    }
    let dataUrl: string;
    try {
      dataUrl = await preparePhoto(file);
    } catch (err) {
      notify.error(err instanceof Error ? err.message : "That image couldn't be used");
      return;
    }
    await run("upload", () => uploadPhoto(dataUrl), "Profile photo updated");
  };

  return (
    <section
      aria-label="Profile photo"
      className="space-y-4 rounded-card border border-border bg-surface-muted/40 p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-foreground">Profile photo</h4>
          <p className="text-sm text-muted-foreground">
            Shown in the top bar. An uploaded photo is cropped to a square.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            ref={fileInput}
            type="file"
            accept="image/*"
            tabIndex={-1}
            aria-hidden="true"
            wrapperClassName="hidden"
            onChange={handleFile}
          />
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<ArrowUpFromLine />}
            loading={busy === "upload"}
            disabled={busy !== null}
            onClick={() => fileInput.current?.click()}
          >
            Upload photo
          </Button>
          {uploaded && (
            <Button
              variant="danger-ghost"
              size="sm"
              leftIcon={<Trash2 />}
              loading={busy === "remove"}
              disabled={busy !== null}
              onClick={() => run("remove", removePhoto, "Uploaded photo removed")}
            >
              Remove upload
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-6 gap-3">
        {profilePics.map((src, index) => (
          <PhotoOption
            key={src}
            src={src}
            label={`Stock photo ${index + 1}`}
            selected={!uploaded && index === stockIndex}
            disabled={busy !== null}
            onSelect={() => run(index, () => chooseStockPhoto(index), "Profile photo updated")}
          />
        ))}
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={onDone} disabled={busy !== null}>
          Done
        </Button>
      </div>
    </section>
  );
};

export default PhotoPicker;
